import { apiService } from '@/api/apiConfig';

// Types for expense categories
export interface ExpenseCategoryData {
  name: string;
  description?: string;
}

export interface ExpenseCategoryResponse {
  id: string;
  name: string;
  description?: string;
}

export interface ExpenseCategoriesResponse {
  data: ExpenseCategoryResponse[];
}

const handleExpenseCategoryError = (error: any): Error => {
  if (error.response?.data?.message) {
    return new Error(`Expense category operation failed: ${error.response.data.message}`);
  }
  return error instanceof Error ? error : new Error('Expense category operation failed');
};

export const expenseRevenueService = {
  getExpenseCategories: async (budgetId: string): Promise<ExpenseCategoriesResponse> => {
    try {
      return await apiService.request<ExpenseCategoriesResponse>({
        method: 'GET',
        url: `/budget/${budgetId}/expense-category`
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  getExpenseCategory: async (budgetId: string, categoryId: string): Promise<ExpenseCategoryResponse> => {
    try {
      return await apiService.request<ExpenseCategoryResponse>({
        method: 'GET',
        url: `/budget/${budgetId}/expense-category/${categoryId}`
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  createExpenseCategory: async (
    budgetId: string,
    categoryData: ExpenseCategoryData
  ): Promise<ExpenseCategoryResponse> => {
    try {
      return await apiService.request<ExpenseCategoryResponse>({
        method: 'POST',
        url: `/budget/${budgetId}/expense-category`,
        data: categoryData,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  activateExpenseCategories: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'POST',
        url: `/budget/${budgetId}/expense-category/activate`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  deactivateExpenseCategory: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'POST',
        url: `/budget/${budgetId}/expense-category/deactivate`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  updateExpenseCategory: async (
    budgetId: string,
    categoryId: string,
    categoryData: ExpenseCategoryData
  ): Promise<ExpenseCategoryResponse> => {
    try {
      return await apiService.request<ExpenseCategoryResponse>({
        method: 'POST',
        url: `/budget/${budgetId}/expense-category/${categoryId}`,
        data: categoryData,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  },

  deleteExpenseCategory: async (budgetId: string, categoryId: string): Promise<void> => {
    try {
      return await apiService.request<void>({
        method: 'DELETE',
        url: `/budget/${budgetId}/expense-category/${categoryId}`
      });
    } catch (error) {
      throw handleExpenseCategoryError(error);
    }
  }
};
